import React, { memo, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import './ConfirmModal.css';

export type ConfirmModalVariant = 'primary' | 'danger' | 'warning' | 'success';

interface ConfirmModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  details?: string;
  variant?: ConfirmModalVariant;
  confirmLabel?: string;
  cancelLabel?: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = memo((props: ConfirmModalProps) => {
  const {
    show,
    onHide,
    onConfirm,
    title,
    message,
    details,
    variant = 'primary',
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
  } = props;
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!show) return;
    const timer = requestAnimationFrame(() => {
      cancelButtonRef.current?.focus();
    });
    return () => cancelAnimationFrame(timer);
  }, [show]);

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          headerClass: 'confirm-modal-header-danger',
          iconClass: 'confirm-icon-danger',
          icon: <i className="bi bi-exclamation-triangle-fill" aria-hidden />,
          confirmButtonVariant: 'danger' as const,
        };
      case 'warning':
        return {
          headerClass: 'confirm-modal-header-warning',
          iconClass: 'confirm-icon-warning',
          icon: <i className="bi bi-exclamation-circle-fill" aria-hidden />,
          confirmButtonVariant: 'warning' as const,
        };
      case 'success':
        return {
          headerClass: 'confirm-modal-header-success',
          iconClass: 'confirm-icon-success',
          icon: <i className="bi bi-check-circle-fill" aria-hidden />,
          confirmButtonVariant: 'success' as const,
        };
      case 'primary':
      default:
        return {
          headerClass: 'confirm-modal-header-primary',
          iconClass: 'confirm-icon-primary',
          icon: <i className="bi bi-question-circle-fill" aria-hidden />,
          confirmButtonVariant: 'primary' as const,
        };
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onHide();
  };

  const styles = getVariantStyles();

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      className="confirm-modal"
      backdropClassName="confirm-modal-backdrop"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-message"
    >
      <Modal.Header closeButton className={styles.headerClass}>
        <div className="d-flex align-items-center">
          <div className={`confirm-icon ${styles.iconClass} me-3`}>
            {styles.icon}
          </div>
          <Modal.Title id="confirm-modal-title" className="mb-0">{title}</Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body className="confirm-modal-body">
        <div id="confirm-modal-message" className="confirm-message">
          <p className="mb-0">{message}</p>
        </div>
        {details && (
          <div className="confirm-details mt-3">
            <div className="confirm-details-header">
              <i className="bi bi-info-circle me-2" aria-hidden />
              <strong>Detalhes:</strong>
            </div>
            <div className="confirm-details-content">
              {details}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="confirm-modal-footer">
        <Button
          ref={cancelButtonRef}
          variant="secondary"
          onClick={onHide}
          className="confirm-button confirm-cancel"
        >
          <i className="bi bi-x-lg me-2" aria-hidden />
          {cancelLabel}
        </Button>
        <Button
          variant={styles.confirmButtonVariant}
          onClick={handleConfirm}
          className="confirm-button confirm-ok"
        >
          <i className="bi bi-check-lg me-2" aria-hidden />
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

ConfirmModal.displayName = 'ConfirmModal';

export default ConfirmModal;
